import React, { useState, useEffect, useRef, useCallback } from 'react';
import { documentsApi, annotationsApi } from '../services/api';
import * as pdfjsLib from 'pdfjs-dist';
import { FileText, Text, Pencil, MessageCircle, HelpCircle, AlertTriangle, CheckCircle, StickyNote } from 'lucide-react';
import './DocumentAnnotator.css';
import FindingsNotes from './FindingsNotes';

pdfjsLib.GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjsLib.version}/pdf.worker.min.js`;



const annotationTypes = [
  { value: 'comment', label: 'Comment', Icon: MessageCircle },
  { value: 'question', label: 'Question', Icon: HelpCircle },
  { value: 'issue', label: 'Issue', Icon: AlertTriangle },
  { value: 'approval', label: 'Approval', Icon: CheckCircle },
];

const annotationCategories = [
  'Clients & Revenue',
  'Front Office (Sales & Delivery)',
  'Operations & Back Office',
  'Legal & Corporate',
  'Finance, Accounting, Tax, QoE, NWC',
  'Valuation/Terms Items',
  'Integration Items',
  'Miscellaneous'
];

function DocumentAnnotator({ documentId, onBack, dealId }) {
  const [selectedText, setSelectedText] = useState('');
  const [showAnnotationForm, setShowAnnotationForm] = useState(false);
  const [annotationText, setAnnotationText] = useState('');
  const [annotationType, setAnnotationType] = useState('comment');
  const [annotationCategory, setAnnotationCategory] = useState(annotationCategories[0]);
  const [documentText, setDocumentText] = useState('');
  const [documentName, setDocumentName] = useState('');
  // eslint-disable-next-line no-unused-vars
  const [previewUrl, setPreviewUrl] = useState('');
  const [loading, setLoading] = useState(true);
  const [availableDocuments, setAvailableDocuments] = useState([]);
  const [selectedDocId, setSelectedDocId] = useState(documentId);
  const [annotations, setAnnotations] = useState([]);
  const [viewMode, setViewMode] = useState('pdf'); // 'pdf' or 'text'
  const [pdfError, setPdfError] = useState(false);
  const [pdfLoaded, setPdfLoaded] = useState(false);
  const [pdfData, setPdfData] = useState(null);
  const [activeTab, setActiveTab] = useState('document'); // 'document' or 'findings'
  const pdfContainerRef = useRef(null);

  useEffect(() => {
    fetchAvailableDocuments();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  
  useEffect(() => {
    loadAnnotations();
  }, []);

  useEffect(() => {
    if (selectedDocId) {
      fetchDocument(selectedDocId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedDocId]);

  const fetchAvailableDocuments = async () => {
    try {
      const response = await documentsApi.list();
      const docs = (response.data.documents || []).map(doc => ({
        jobId: doc.id,
        filename: doc.filename,
        ...doc
      }));
      setAvailableDocuments(docs);
      if (docs.length > 0 && !selectedDocId) {
        setSelectedDocId(docs[0].jobId);
      }
    } catch (error) {
      console.error('Failed to fetch documents:', error);
    }
  };

  const fetchDocument = async (id) => {
    setLoading(true);
    try {
      // Fetch document text
      const textRes = await documentsApi.getText(id);
      
      setDocumentText(textRes.data.text);
      setDocumentName(textRes.data.filename);
      
      const previewUrl = documentsApi.getPreviewUrl(id);
      setPreviewUrl(previewUrl);
      setPdfError(false);
      setPdfLoaded(false);
      fetchPdfData(previewUrl);
    } catch (error) {
      console.error('Failed to fetch document:', error);
      setDocumentText('Failed to load document. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const fetchPdfData = async (url) => {
    try {
      const response = await fetch(url);
      if (!response.ok) {
        setPdfError(true);
        return;
      }
      const arrayBuffer = await response.arrayBuffer();
      setPdfData(new Uint8Array(arrayBuffer));
    } catch (err) {
      console.error('PDF fetch error:', err);
      setPdfError(true);
    }
  };

  const renderPdfToContainer = useCallback(async () => {
    if (!pdfData || !pdfContainerRef.current) return;
    try {
      const pdf = await pdfjsLib.getDocument({ data: pdfData.slice() }).promise;
      const container = pdfContainerRef.current;
      if (!container) return;
      container.innerHTML = '';

      const containerWidth = container.clientWidth - 24;

      for (let i = 1; i <= pdf.numPages; i++) {
        const page = await pdf.getPage(i);
        const defaultViewport = page.getViewport({ scale: 1 });
        const scale = Math.min(containerWidth / defaultViewport.width, 1.5);
        const viewport = page.getViewport({ scale });

        const pageWrapper = document.createElement('div');
        pageWrapper.className = 'pdf-page-wrapper';
        pageWrapper.style.position = 'relative';
        pageWrapper.style.width = `${viewport.width}px`;
        pageWrapper.style.height = `${viewport.height}px`;
        pageWrapper.style.marginBottom = '12px';

        const canvas = document.createElement('canvas');
        canvas.width = viewport.width;
        canvas.height = viewport.height;
        canvas.className = 'pdf-page-canvas';
        pageWrapper.appendChild(canvas);

        const context = canvas.getContext('2d');
        await page.render({ canvasContext: context, viewport }).promise;

        // Render text layer for selection
        const textContent = await page.getTextContent();
        const textLayerDiv = document.createElement('div');
        textLayerDiv.className = 'pdf-text-layer';
        textLayerDiv.style.position = 'absolute';
        textLayerDiv.style.top = '0';
        textLayerDiv.style.left = '0';
        textLayerDiv.style.width = `${viewport.width}px`;
        textLayerDiv.style.height = `${viewport.height}px`;
        pageWrapper.appendChild(textLayerDiv);

        const textLayer = new pdfjsLib.TextLayer({
          textContentSource: textContent,
          container: textLayerDiv,
          viewport: viewport
        });
        await textLayer.render();

        container.appendChild(pageWrapper);
      }
      setPdfLoaded(true);
    } catch (err) {
      console.error('PDF render error:', err);
      setPdfError(true);
    }
  }, [pdfData]);

  useEffect(() => {
    if (pdfData && viewMode === 'pdf') {
      setPdfLoaded(false);
      const timer = setTimeout(() => renderPdfToContainer(), 50);
      return () => clearTimeout(timer);
    }
  }, [pdfData, viewMode, renderPdfToContainer]);

  const loadAnnotations = () => {
    const stored = localStorage.getItem('dealnexa_annotations');
    if (stored) {
      setAnnotations(JSON.parse(stored));
    }
  };

  const saveAnnotations = (newAnnotations) => {
    localStorage.setItem('dealnexa_annotations', JSON.stringify(newAnnotations));
    setAnnotations(newAnnotations);
  };

  const handleTextSelection = useCallback(() => {
    const selection = window.getSelection();
    const text = selection.toString().trim();
    
    if (text.length > 0) {
      console.log('Text selected:', text.substring(0, 50) + '...');
      setSelectedText(text);
      setShowAnnotationForm(true);
    }
  }, []);

  const handleSaveAnnotation = async () => {
    if (selectedText && annotationText) {
      const newAnnotation = {
        id: Date.now(),
        documentId: selectedDocId,
        selectedText,
        text: annotationText,
        type: annotationType,
        category: annotationCategory,
        createdAt: new Date().toISOString(),
        author: 'Current User'
      };
      
      // Save to backend
      try {
        await annotationsApi.create({
          document_id: selectedDocId,
          selected_text: selectedText,
          text: annotationText,
          type: annotationType,
          category: annotationCategory
        });
      } catch (error) {
        console.error('Failed to save annotation to backend:', error);
      }
      
      const updatedAnnotations = [...annotations, newAnnotation];
      saveAnnotations(updatedAnnotations);
      
      setAnnotationText('');
      setShowAnnotationForm(false);
      setSelectedText('');
      setAnnotationType('comment');
      setAnnotationCategory(annotationCategories[0]);
      window.getSelection().removeAllRanges();
    }
  };

  const handleCancel = () => {
    setAnnotationText('');
    setShowAnnotationForm(false);
    setSelectedText('');
    setAnnotationType('comment');
    window.getSelection().removeAllRanges();
  };

  const handleDeleteAnnotation = async (id) => {
    try {
      await annotationsApi.delete(id);
    } catch (error) {
      console.error('Failed to delete annotation:', error);
    }
    
    const updatedAnnotations = annotations.filter(a => a.id !== id);
    saveAnnotations(updatedAnnotations);
  };

  const getAnnotationIcon = (type) => {
    const found = annotationTypes.find(t => t.value === type);
    const IconComponent = found ? found.Icon : MessageCircle;
    return <IconComponent size={14} />;
  };

  // Filter annotations for current document
  const documentAnnotations = annotations.filter(a => a.documentId === selectedDocId);

  return (
    <div className="annotations-container">
      {/* Tab Navigation */}
      <div className="annotations-tabs">
        <button 
          className={`annotation-tab ${activeTab === 'document' ? 'active' : ''}`}
          onClick={() => setActiveTab('document')}
        >
          <FileText size={16} />
          Document View
        </button>
        <button 
          className={`annotation-tab ${activeTab === 'findings' ? 'active' : ''}`}
          onClick={() => setActiveTab('findings')}
        >
          <StickyNote size={16} />
          Findings Notes
        </button>
      </div>

      {activeTab === 'findings' ? (
        <FindingsNotes dealId={dealId} />
      ) : (
        <>
          <div className="annotations-header">
            <div className="annotations-header-left">
              <h2>{documentName || 'Select a document'}</h2>
              <span className="annotations-subtitle">
                {viewMode === 'pdf' ? 'View PDF and select text to annotate' : 'Select text to add annotations'}
              </span>
            </div>
            <div className="annotations-header-right">
              <div className="view-mode-toggle">
                <button 
                  className={`view-mode-btn ${viewMode === 'pdf' ? 'active' : ''}`}
                  onClick={() => setViewMode('pdf')}
                >
                  <FileText size={16} /> PDF
                </button>
                <button 
                  className={`view-mode-btn ${viewMode === 'text' ? 'active' : ''}`}
                  onClick={() => setViewMode('text')}
                >
                  <Text size={16} /> Text
                </button>
              </div>
            </div>
          </div>

          <div className="annotations-content">
        <div className="document-viewer">
          <div className="document-viewer-content">
            {loading ? (
              <div className="loading-document">
                <div className="spinner"></div>
                <p>Loading document...</p>
              </div>
            ) : viewMode === 'pdf' && !pdfError ? (
              <>
                <div className="pdf-canvas-container" ref={pdfContainerRef} onMouseUp={handleTextSelection}>
                </div>
                {!pdfLoaded && (
                  <div className="loading-document">
                    <div className="spinner"></div>
                    <p>Rendering PDF...</p>
                  </div>
                )}
              </>
            ) : (
              <div 
                className="document-text"
                onMouseUp={handleTextSelection}
                onClick={handleTextSelection}
                style={{ userSelect: 'text', cursor: 'text' }}
              >
                {documentText}
              </div>
            )}
          </div>
        </div>

        <div className="annotations-sidebar">
          <div className="document-selector">
            <label>Document</label>
            <select 
              className="document-select"
              value={selectedDocId || ''}
              onChange={(e) => setSelectedDocId(e.target.value)}
            >
              {availableDocuments.map(doc => (
                <option key={doc.jobId} value={doc.jobId}>{doc.filename}</option>
              ))}
            </select>
          </div>

          <div className="annotations-sidebar-header">
            <h3>Annotations</h3>
            <span className="annotations-count">{documentAnnotations.length}</span>
          </div>

          <div className="annotations-sidebar-content">
            {showAnnotationForm && (
              <div className="annotation-form">
                <div className="selected-text-box">
                  <div className="selected-text-label">Selected Text:</div>
                  <div className="selected-text-content">
                    "{selectedText.substring(0, 150)}{selectedText.length > 150 ? '...' : ''}"
                  </div>
                </div>

                <div className="form-group">
                  <label>Annotation Type</label>
                  <select 
                    className="annotation-type-select"
                    value={annotationType} 
                    onChange={(e) => setAnnotationType(e.target.value)}
                  >
                    {annotationTypes.map(type => (
                      <option key={type.value} value={type.value}>{type.label}</option>
                    ))}
                  </select>
                </div>

                <div className="form-group">
                  <label>Category</label>
                  <select 
                    className="annotation-category-select"
                    value={annotationCategory} 
                    onChange={(e) => setAnnotationCategory(e.target.value)}
                  >
                    {annotationCategories.map(cat => (
                      <option key={cat} value={cat}>{cat}</option>
                    ))}
                  </select>
                </div>

                <div className="form-group">
                  <label>@ Mention Team Member</label>
                  <select 
                    className="team-member-select"
                    defaultValue=""
                  >
                    <option value="">Select team member...</option>
                    <option value="user1">Current User</option>
                  </select>
                </div>

                <div className="form-group">
                  <label>Add your annotation or note...</label>
                  <textarea
                    className="annotation-textarea"
                    value={annotationText}
                    onChange={(e) => setAnnotationText(e.target.value)}
                    placeholder="Enter your note..."
                    rows={3}
                  />
                </div>

                <div className="form-actions">
                  <button className="btn-save" onClick={handleSaveAnnotation}>
                    💬 Save
                  </button>
                  <button className="btn-cancel" onClick={handleCancel}>
                    Cancel
                  </button>
                </div>
              </div>
            )}

            {documentAnnotations.length === 0 && !showAnnotationForm ? (
              <div className="annotations-empty">
                <div className="annotations-empty-icon"><Pencil size={32} /></div>
                <h4>No annotations yet</h4>
                <p>Select text in the document to add annotations</p>
              </div>
            ) : (
              <div className="annotations-list">
                {documentAnnotations.map((annotation) => (
                  <div key={annotation.id} className={`annotation-item ${annotation.type}`}>
                    <div className="annotation-item-header">
                      <span className="annotation-type-icon">{getAnnotationIcon(annotation.type)}</span>
                      <span className="annotation-author">{annotation.author}</span>
                      <span className="annotation-time">
                        {new Date(annotation.createdAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                      </span>
                      <button 
                        className="annotation-delete-btn"
                        onClick={() => handleDeleteAnnotation(annotation.id)}
                      >
                        ×
                      </button>
                    </div>
                    <div className="annotation-selected-text">
                      "{annotation.selectedText.substring(0, 100)}{annotation.selectedText.length > 100 ? '...' : ''}"
                    </div>
                    <div className="annotation-text">{annotation.text}</div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
        </>
      )}
    </div>
  );
}

export default DocumentAnnotator;
